import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Server, CheckCircle2, XCircle, FileCheck, Database, Key } from 'lucide-react';
/*
  SEO SPECIFICATION:
  Title: MindStream.ing Security — Enterprise-Grade Protection Built on Google Cloud
  Meta Desc: Enterprise-grade security built on Google Cloud Platform. Your data is encrypted, monitored, and never shared. SOC2 aligned. GDPR/CCPA ready.
  Primary KW: AI automation security compliance
*/
export function Security() {
  return <main className="pt-20" data-id="element-2269" data-mp="3kour1v94egy4">
      {/* Hero */}
      <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-28 px-6 bg-hero-gradient overflow-hidden" data-id="element-2270" data-mp="3ksaa195x0a4t">
        {/* Ambient Glow Orbs */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-2271" data-mp="3asjdz31phn2m" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-2272" data-mp="1dyokcb18a6hq" />

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} data-id="element-2273" data-mp="1qgbwek60s0oe" />

        <div className="inner-container relative z-10 text-center max-w-4xl mx-auto" data-id="element-2274" data-mp="1li0inyclt60w">
          <span className="eyebrow" data-id="element-2275" data-mp="1ljv6plj69e97">Security & Trust</span>
          <h1 className="font-poppins font-extrabold text-5xl md:text-6xl leading-[1.1] mb-6" data-id="element-2276" data-mp="298xmenu8wx12">
            Your Business Data.
            <br data-id="element-2277" data-mp="2e8sc4o2abgkj" />
            <span className="text-gradient" data-id="element-2278" data-mp="1zht7tp8hfe6r">Protected. Always.</span>
          </h1>
          <p className="text-lg md:text-xl text-steel mb-10 leading-relaxed max-w-2xl mx-auto" data-id="element-2279" data-mp="1zfp4p9ieqj7j">
            Enterprise-grade security built on Google Cloud Platform. Your data
            is encrypted, monitored, and never shared.
          </p>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-2280" data-mp="2wz0ev522o507" />

      {/* Trust Badges */}
      <section className="py-12 bg-dark-bg relative" data-id="element-2281" data-mp="31ucje6f8bbwc">
        <div className="inner-container relative z-10" data-id="element-2282" data-mp="31uw0xm0yvy9u">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-2283" data-mp="2wugmer4y8kqa">
            {[{
            title: 'SOC2 Aligned',
            icon: <FileCheck size={24} data-id="element-2284" data-mp="2st4yqr5my9kq" />
          }, {
            title: 'GDPR Ready',
            icon: <Globe size={24} data-id="element-2285" data-mp="2nw4rwe0yp2sk" />
          }, {
            title: 'CCPA Compliant',
            icon: <Shield size={24} data-id="element-2286" data-mp="2e48ruxfbf2lp" />
          }, {
            title: '256-bit Encryption',
            icon: <Lock size={24} data-id="element-2287" data-mp="2966x3f8j1eue" />
          }].map((badge, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex flex-col items-center justify-center gap-3 p-6 bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm rounded-xl border border-white/10 hover:border-teal/30 hover:shadow-[0_0_20px_rgba(7,133,183,0.1)] transition-all" data-id="element-2288" data-mp="253k9jqiw2g30" data-mpi={i}>
                <div className="text-teal bg-teal/10 p-3 rounded-full shadow-[0_0_15px_rgba(7,133,183,0.2)]" data-id="element-2289" data-mp="xiugkiiiy9q">
                  {badge.icon}
                </div>
                <span className="font-poppins font-semibold text-sm text-center" data-id="element-2290" data-mp="5vkb6ysguli1">
                  {badge.title}
                </span>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-2291" data-mp="3ebx289a4bj33" />

      {/* Infrastructure */}
      <section className="section-padding bg-dark-bg relative overflow-hidden" data-id="element-2292" data-mp="1kurf7b91wv19">
        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal/10 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-2293" data-mp="1fwg46x70sehg" />

        <div className="inner-container relative z-10" data-id="element-2294" data-mp="1ouib1tdfq1cn">
          <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-2295" data-mp="23kh63n9p0wnw">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-2296" data-mp="23imke6qll0ir">
              <span className="eyebrow" data-id="element-2297" data-mp="28kbwlppk3k8f">Infrastructure</span>
              <h2 className="h2-title" data-id="element-2298" data-mp="i6cseu1bqqe4">Built on Google Cloud</h2>
              <p className="text-steel mb-6 leading-relaxed" data-id="element-2299" data-mp="e5j2xw88h3j4">
                MindStream.ing is hosted securely on Google Cloud Platform (GCP
                us-central1), leveraging the same infrastructure that powers
                Google's own products.
              </p>
              <ul className="space-y-4" data-id="element-2300" data-mp="82effg50ilr7">
                <li className="flex items-start gap-3" data-id="element-2301" data-mp="3qhp2eemf6f5a">
                  <Server className="text-teal shrink-0 mt-1" size={20} data-id="element-2302" data-mp="3qgx8r9cdurhn" />
                  <div data-id="element-2303" data-mp="3vez00hjypx47">
                    <h4 className="font-bold text-white" data-id="element-2304" data-mp="3vefekqv3sqlf">US-Based Hosting</h4>
                    <p className="text-sm text-steel" data-id="element-2305" data-mp="3gi7v5srirt77">
                      All primary data is stored and processed in GCP
                      us-central1.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-2306" data-mp="37ke692w341u2">
                  <Lock className="text-teal shrink-0 mt-1" size={20} data-id="element-2307" data-mp="3cifknaikfcz7" />
                  <div data-id="element-2308" data-mp="15r7twwboj5oy">
                    <h4 className="font-bold text-white" data-id="element-2309" data-mp="15rzoc3tjixdn">
                      End-to-End Encryption
                    </h4>
                    <p className="text-sm text-steel" data-id="element-2310" data-mp="1ap9n4a2tkle5">
                      Data is encrypted at rest (AES-256) and in transit (TLS
                      1.3).
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-2311" data-mp="zaoex0w4t097">
                  <Database className="text-teal shrink-0 mt-1" size={20} data-id="element-2312" data-mp="zbhg7mcbmxjy" />
                  <div data-id="element-2313" data-mp="ucek4rebk1o4">
                    <h4 className="font-bold text-white" data-id="element-2314" data-mp="ud63n3un68y1">99.9% Uptime SLA</h4>
                    <p className="text-sm text-steel" data-id="element-2315" data-mp="1rybv83qdd4q1">
                      Redundant architecture ensures your business never stops
                      running.
                    </p>
                  </div>
                </li>
              </ul>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-md border border-white/10 rounded-2xl p-8 shadow-[0_0_40px_rgba(0,0,0,0.5)] relative overflow-hidden" data-id="element-2316" data-mp="1daqrc7lo71h2">
              <div className="absolute top-0 right-0 w-64 h-64 bg-teal/20 rounded-full blur-3xl pointer-events-none" data-id="element-2317" data-mp="2n8gvmrvibula"></div>
              <h3 className="font-poppins font-bold text-xl mb-6 relative z-10" data-id="element-2318" data-mp="2s7tpvxopk3xw">
                Access Controls
              </h3>
              <ul className="space-y-6 relative z-10" data-id="element-2319" data-mp="2w8lzis1hsaup">
                <li className="flex items-center gap-4 border-b border-white/10 pb-4" data-id="element-2320" data-mp="317q7xggle4jz">
                  <div className="bg-purple/20 p-2 rounded-lg text-purple" data-id="element-2321" data-mp="35z89o6qfw960">
                    <Key size={20} data-id="element-2322" data-mp="3611fxkx54rdh" />
                  </div>
                  <div data-id="element-2323" data-mp="3awq996ikhc1c">
                    <div className="font-semibold" data-id="element-2324" data-mp="3ax9q0jwhduds">
                      Role-Based Access (RBAC)
                    </div>
                    <div className="text-xs text-steel" data-id="element-2325" data-mp="3jyps84d56q6x">
                      Granular permissions for your team
                    </div>
                  </div>
                </li>
                <li className="flex items-center gap-4 border-b border-white/10 pb-4" data-id="element-2326" data-mp="96d9e90mq5bl">
                  <div className="bg-purple/20 p-2 rounded-lg text-purple" data-id="element-2327" data-mp="52fbg1n1j0k4">
                    <Shield size={20} data-id="element-2328" data-mp="j158jh9v108h" />
                  </div>
                  <div data-id="element-2329" data-mp="e3mxnlca8axu">
                    <div className="font-semibold" data-id="element-2330" data-mp="e4es2su582mj">
                      Two-Factor Authentication
                    </div>
                    <div className="text-xs text-steel" data-id="element-2331" data-mp="3mjgclvqytecs">
                      Enforced 2FA for all admin accounts
                    </div>
                  </div>
                </li>
                <li className="flex items-center gap-4" data-id="element-2332" data-mp="4dqtc9yut6j">
                  <div className="bg-purple/20 p-2 rounded-lg text-purple" data-id="element-2333" data-mp="3rijhng6y3kkr">
                    <FileCheck size={20} data-id="element-2334" data-mp="3rggju9zjwn5h" />
                  </div>
                  <div data-id="element-2335" data-mp="33re1ezx3eq9x">
                    <div className="font-semibold" data-id="element-2336" data-mp="33qm5fnzl2qj4">Audit Logging</div>
                    <div className="text-xs text-steel" data-id="element-2337" data-mp="2yst3j75bcion">
                      Track every action taken by users and MISTR
                    </div>
                  </div>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-2338" data-mp="15cgd4h9hr3r8" />

      {/* Data Handling */}
      <section className="section-padding bg-dark-bg relative" data-id="element-2339" data-mp="19ae3voiwtxwq">
        <div className="inner-container relative z-10" data-id="element-2340" data-mp="1x3cpzhvjx8ei">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2341" data-mp="220w32egijf4v">
            <span className="eyebrow" data-id="element-2342" data-mp="220cu3n8uc12z">Data Privacy</span>
            <h2 className="h2-title" data-id="element-2343" data-mp="1o263ms6813dc">What We Store (And What We Don't)</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-2344" data-mp="1szy62g4hs7vs">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-gradient-to-br from-teal/10 to-transparent backdrop-blur-sm border border-teal/30 rounded-2xl p-8 shadow-[0_0_30px_rgba(7,133,183,0.1)]" data-id="element-2345" data-mp="cm7r2ebu3z7d">
              <div className="flex items-center gap-3 mb-6" data-id="element-2346" data-mp="3941musokxg6w">
                <CheckCircle2 className="text-teal drop-shadow-[0_0_8px_rgba(7,133,183,0.5)]" size={28} data-id="element-2347" data-mp="3e34m1whvos6y" />
                <h3 className="font-poppins font-bold text-xl" data-id="element-2348" data-mp="2z8hs49lr4njq">
                  What MR Stores
                </h3>
              </div>
              <p className="text-steel text-sm mb-6" data-id="element-2349" data-mp="2q68j5o64t28g">
                Data required to run your business operations effectively.
              </p>
              <ul className="space-y-3" data-id="element-2350" data-mp="2v51yzn8w55bs">
                <li className="flex items-center gap-2 text-sm" data-id="element-2351" data-mp="oelsjnq5j9ci">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal" data-id="element-2352" data-mp="oc99t8ckkuye"></div>{' '}
                  Business context & brand voice
                </li>
                <li className="flex items-center gap-2 text-sm" data-id="element-2353" data-mp="13gki49ph39pd">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal" data-id="element-2354" data-mp="yi973vnfyt5k"></div> Task
                  data and project status
                </li>
                <li className="flex items-center gap-2 text-sm" data-id="element-2355" data-mp="1cape17jgrbbr">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal" data-id="element-2356" data-mp="17iyx2iq0tpbd"></div>{' '}
                  Communication logs (emails/SMS sent via MR)
                </li>
                <li className="flex items-center gap-2 text-sm" data-id="element-2357" data-mp="1m60j0y52l46b">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal" data-id="element-2358" data-mp="1h9jyedx2x7xz"></div> CRM
                  contact details
                </li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-gradient-to-br from-red-cta/10 to-transparent backdrop-blur-sm border border-red-cta/30 rounded-2xl p-8 shadow-[0_0_30px_rgba(255,0,10,0.1)]" data-id="element-2359" data-mp="1r77sd1mv2bf3">
              <div className="flex items-center gap-3 mb-6" data-id="element-2360" data-mp="25v1ux3dcob0q">
                <XCircle className="text-red-cta drop-shadow-[0_0_8px_rgba(255,0,10,0.5)]" size={28} data-id="element-2361" data-mp="2avh4djuigydf" />
                <h3 className="font-poppins font-bold text-xl" data-id="element-2362" data-mp="2ew9gqlyojjdx">
                  What MR NEVER Stores
                </h3>
              </div>
              <p className="text-steel text-sm mb-6" data-id="element-2363" data-mp="2olsy1n6qv56o">
                Sensitive data that MR does not need to retain.
              </p>
              <ul className="space-y-3" data-id="element-2364" data-mp="2tk4am5ofbtsl">
                <li className="flex items-center gap-2 text-sm" data-id="element-2365" data-mp="2ykihvli7azpk">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-cta" data-id="element-2366" data-mp="2ym3dzfk5yqpj"></div>{' '}
                  Raw passwords or credentials
                </li>
                <li className="flex items-center gap-2 text-sm" data-id="element-2367" data-mp="qyihf2v9ed25">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-cta" data-id="element-2368" data-mp="qwntrgsls6ud"></div>{' '}
                  Full payment information (CC numbers)
                </li>
                <li className="flex items-center gap-2 text-sm" data-id="element-2369" data-mp="hz53ny03r2bm">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-cta" data-id="element-2370" data-mp="cz8yhvfo54uh"></div>{' '}
                  Personal health data (PHI)
                </li>
                <li className="flex items-center gap-2 text-sm" data-id="element-2371" data-mp="1alg5fbzg39a0">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-cta" data-id="element-2372" data-mp="15n4uexxeysq7"></div>{' '}
                  Unrelated personal communications
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-2373" data-mp="vw0nmlhw6e6g" />

      {/* Enterprise Add-ons */}
      <section className="section-padding bg-dark-bg relative overflow-hidden" data-id="element-2374" data-mp="1om0kpy65imyr">
        {/* Ambient Glow */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-purple/10 rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-2375" data-mp="1949benuhuknv" />

        <div className="inner-container max-w-4xl mx-auto relative z-10" data-id="element-2376" data-mp="1d3rpaznwdvh7">
          <div className="bg-gradient-to-br from-purple/10 to-teal/10 backdrop-blur-md border border-purple/30 rounded-2xl p-10 text-center shadow-[0_0_40px_rgba(124,58,237,0.15)]" data-id="element-2377" data-mp="z85yqo20n3mo">
            <h3 className="font-poppins font-bold text-2xl mb-4" data-id="element-2378" data-mp="1rwboxqbu9ktw">
              Enterprise Security Add-ons
            </h3>
            <p className="text-steel mb-8 max-w-2xl mx-auto" data-id="element-2379" data-mp="1ww6b5gko5m8m">
              Need stricter compliance? Our Enterprise plan includes advanced
              security features for larger organizations.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8" data-id="element-2380" data-mp="2atuycv27l5gu">
              <span className="bg-white/5 px-4 py-2 rounded-lg text-sm" data-id="element-2381" data-mp="2asjtst0xvtoo">
                Custom Data Retention
              </span>
              <span className="bg-white/5 px-4 py-2 rounded-lg text-sm" data-id="element-2382" data-mp="45m7vbtltblt">
                Dedicated Infrastructure
              </span>
              <span className="bg-white/5 px-4 py-2 rounded-lg text-sm" data-id="element-2383" data-mp="3vaue4oy67pry">
                Annual Security Review
              </span>
              <span className="bg-white/5 px-4 py-2 rounded-lg text-sm" data-id="element-2384" data-mp="cx4kxdv97bao">
                BAA Available
              </span>
            </div>
            <button className="btn-primary" data-id="element-2385" data-mp="3hburtny4rmyy">
              Questions about security? Talk to MR →
            </button>
          </div>
        </div>
      </section>
    </main>;
}
// Helper component for the Globe icon that was missing from lucide-react import
function Globe(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2386" data-mp="3ldzj4g13w7if">
      <circle cx="12" cy="12" r="10" data-id="element-2387" data-mp="10n8mzgssn2zk" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" data-id="element-2388" data-mp="3ufqglhvm91zm" />
      <path d="M2 12h20" data-id="element-2389" data-mp="38npeqfs2vpe" />
    </svg>;
}